// Costs (docs/12 "Execution as built", Costs): every user's agent spend for admins with
// ViewAllCosts, over a rolling day, week, month or all time (in the URL): stat cards, a daily
// chart split by whose key paid, and sortable tables by user, task group and task.
import { Coins } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { usersQuery } from "../auth/admin";
import { usePermissions } from "../auth/permissions";
import { adminCostsQuery } from "../costs/api";
import { CostStats, PeriodSwitch } from "../costs/CostStats";
import { CostTables, type LineKind } from "../costs/CostTables";
import { calendarDay, dailySeries, parsePeriod, type Period } from "../costs/model";
import { PaidSpendBars } from "../dashboard/lazyCharts";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { dayMonth, shortId } from "../pages/format";
import { useT } from "../i18n";
import { WidgetCard } from "../ui/Card";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";

const KINDS: LineKind[] = ["users", "groups", "tasks"];

export default function CostsPage() {
  const t = useT().costs;
  const crumbs = sectionCrumbs("costs");
  const [params, setParams] = useSearchParams();
  const period = parsePeriod(params.get("period"));
  const kind = KINDS.find((k) => k === params.get("by")) ?? "users";
  const report = useQuery(adminCostsQuery(period));
  const { can } = usePermissions();
  const users = useQuery({ ...usersQuery(), enabled: can("ViewUsers") });
  const names = new Map((users.data ?? []).map((u) => [u.id, u.display_name || u.email]));
  const set = (patch: { period?: Period; by?: LineKind }) => setParams({ period, by: kind, ...patch }, { replace: true });
  // The series runs to the day the report was fetched.
  const series = report.data ? dailySeries(report.data, report.dataUpdatedAt) : [];

  return (
    <>
      <PageHeader title={SECTIONS.costs.title} breadcrumb={crumbs} description={SECTIONS.costs.subtitle}>
        <PeriodSwitch value={period} onChange={(p) => set({ period: p })} />
      </PageHeader>
      {report.isPending ? <Loading /> : null}
      {report.isError ? <ErrorState title={t.loadFailed} message={report.error.message} /> : null}
      {report.data ? (
        <div className="space-y-5">
          <CostStats report={report.data} />
          <WidgetCard title={t.dailySpend} description={t.dailySpendDescription}>
            {series.length === 0 ? (
              <EmptyState compact icon={<Coins weight="duotone" />} title={t.nothingSpent}>
                {t.nothingSpentBody}
              </EmptyState>
            ) : (
              <div className="h-[220px]">
                <PaidSpendBars data={series.map((d) => ({ day: dayMonth(calendarDay(d.start)), org: d.org, platform: d.platform }))} />
              </div>
            )}
          </WidgetCard>
          <WidgetCard label={t.costLines}>
            <CostTables report={report.data} kind={kind} onKind={(by) => set({ by })} userName={(id) => names.get(id) ?? shortId(id)} />
          </WidgetCard>
        </div>
      ) : null}
    </>
  );
}
